import { supabase, checkConnection as testSupabase } from './supabaseClient';
import { storage, STORAGE_KEYS } from './storageService';

export const dbAdapter = {
  mode: 'supabase',

  // ============================================
  // GET
  // ============================================
  async get(table) {
    try {
      console.log(`📥 Fetching ${table}...`);
      const { data, error } = await supabase.from(table).select('*');
      
      if (error) {
        console.error(`❌ Supabase error (${table}):`, error.message);
        const fallbackData = storage.get(STORAGE_KEYS[table.toUpperCase()]) || [];
        console.log(`📥 Fallback to localStorage: ${fallbackData.length} items`);
        return fallbackData;
      }
      
      console.log(`✅ ${table} loaded: ${data?.length || 0} items`);
      return data || [];
    } catch (error) {
      console.error(`❌ Get error (${table}):`, error.message);
      return storage.get(STORAGE_KEYS[table.toUpperCase()]) || [];
    }
  },

  // ============================================
  // GET BY ID
  // ============================================
  async getById(table, id) {
    try {
      console.log(`📥 Fetching ${table} with id: ${id}`);
      const { data, error } = await supabase
        .from(table)
        .select()
        .eq('id', id);
      
      if (error) {
        console.error(`❌ Supabase error (${table}):`, error.message);
        return storage.getById(STORAGE_KEYS[table.toUpperCase()], id);
      }
      
      const result = data && data.length > 0 ? data[0] : null;
      console.log(`✅ ${table} found:`, result ? 'Yes' : 'No');
      return result;
    } catch (error) {
      console.error(`❌ GetById error (${table}):`, error.message);
      return storage.getById(STORAGE_KEYS[table.toUpperCase()], id);
    }
  },

  // ============================================
  // CREATE
  // ============================================
  async create(table, item) {
    try {
      console.log(`📤 Creating ${table}:`, item.id || 'new');
      console.log('📤 Data:', JSON.stringify(item, null, 2));
      
      const { data, error } = await supabase
        .from(table)
        .insert([item])
        .select();
      
      if (error) {
        console.error('❌ Supabase error:', JSON.stringify(error, null, 2));
        console.error('❌ Error code:', error.code);
        console.error('❌ Error message:', error.message);
        console.error('❌ Error details:', error.details);
        console.error('❌ Error hint:', error.hint);
        return storage.add(STORAGE_KEYS[table.toUpperCase()], item);
      }
      
      console.log('✅ Created:', data);
      const result = data && data.length > 0 ? data[0] : null;
      return result;
    } catch (error) {
      console.error('❌ Create error:', error);
      return storage.add(STORAGE_KEYS[table.toUpperCase()], item);
    }
  },

  // ============================================
  // UPDATE - FULL CAMELCASE (TANPA FILTER)
  // ============================================
  async update(table, id, updates) {
    try {
      console.log(`📤 Updating ${table}:`, id);
      console.log('📤 RAW UPDATES:', JSON.stringify(updates, null, 2));

      // 🔥 LANGSUNG UPDATE, TANPA FILTER APAPUN
      const { data, error } = await supabase
        .from(table)
        .update(updates)  // ← PAKE UPDATES LANGSUNG
        .eq('id', id)
        .select();
      
      if (error) {
        console.error(`❌ Supabase error (${table}):`, error.message);
        console.error('❌ Error details:', error.details);
        console.error('❌ Error hint:', error.hint);
        console.error('❌ Failed updates:', JSON.stringify(updates, null, 2));
        
        // Fallback ke localStorage
        return storage.update(STORAGE_KEYS[table.toUpperCase()], id, updates);
      }
      
      const result = data && data.length > 0 ? data[0] : null;
      console.log('✅ Updated:', result);
      return result;
    } catch (error) {
      console.error(`❌ Update error (${table}):`, error.message);
      return storage.update(STORAGE_KEYS[table.toUpperCase()], id, updates);
    }
  },

  // ============================================
  // DELETE
  // ============================================
  async delete(table, id) {
    try {
      console.log(`📤 Deleting ${table}:`, id);
      const { error } = await supabase
        .from(table)
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error(`❌ Supabase error (${table}):`, error.message);
        return storage.delete(STORAGE_KEYS[table.toUpperCase()], id);
      }
      
      console.log('✅ Deleted:', id);
      return true;
    } catch (error) {
      console.error(`❌ Delete error (${table}):`, error.message);
      return storage.delete(STORAGE_KEYS[table.toUpperCase()], id);
    }
  },

  // ============================================
  // UTILITY
  // ============================================
  async testConnection() {
    return await testSupabase();
  },

  async migrateAll() {
    const tables = ['customers', 'staff', 'work_orders', 'inventory', 'transactions'];
    const results = {};

    for (const table of tables) {
      try {
        const storageKey = table.toUpperCase();
        const data = storage.get(STORAGE_KEYS[storageKey]) || [];
        
        if (data.length > 0) {
          const { error } = await supabase
            .from(table)
            .upsert(data, { onConflict: 'id' });
          
          if (error) throw error;
          results[table] = { success: true, count: data.length };
        } else {
          results[table] = { success: true, count: 0 };
        }
      } catch (error) {
        results[table] = { success: false, error: error.message };
      }
    }
    return results;
  },

  setMode(mode) {
    this.mode = mode;
    localStorage.setItem('eos7-db-mode', mode);
  },

  getMode() {
    return this.mode;
  }
};